import React, { useState, useEffect } from "react";
import { Card, Table, DatePicker, Button, Space, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import ReportService from "../../services/ReportService";

const { RangePicker } = DatePicker;

type CompanyProposalStats = {
  firstName: string;
  lastName: string;
  email: string;
  accountId: number;
  total: number;
  withProposal: number;
  withoutProposal: number;
  proposalRatio: number; // 0-100
};

const columns: ColumnsType<CompanyProposalStats> = [
  {
    title: "Ad Soyad / E-posta",
    render: (_, r) => (
      <>
        {r.firstName} {r.lastName}
        <br />
        <span style={{ color: "#888", fontSize: 12 }}>{r.email}</span>
      </>
    ),
    key: "adsoyad",
    width: 220,
    fixed: "left"
  },
  {
    title: "Yeni Firma",
    dataIndex: "total",
    key: "total",
    align: "center" as "center",
    width: 120,
    render: (v: number) =>
      v > 0 ? <b>{v}</b> : <span style={{ color: "#aaa" }}>{v}</span>
  },
  {
    title: "Teklif Verilen",
    dataIndex: "withProposal",
    key: "withProposal",
    align: "center" as "center",
    width: 150,
    render: (v: number) =>
      v > 0 ? <b>{v}</b> : <span style={{ color: "#aaa" }}>{v}</span>
  },
  {
    title: "Teklif Verilmeyen",
    dataIndex: "withoutProposal",
    key: "withoutProposal",
    align: "center" as "center",
    width: 170,
    render: (v: number) =>
      v > 0 ? <b>{v}</b> : <span style={{ color: "#aaa" }}>{v}</span>
  },
  {
    title: "Teklif Oranı",
    dataIndex: "proposalRatio",
    key: "proposalRatio",
    align: "center" as "center",
    width: 110,
    render: (v: number) =>
      v > 0 ? <b>%{v}</b> : <span style={{ color: "#aaa" }}>%0</span>
  }
];

const CompanyProposalStatsTable: React.FC = () => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("month"),
    dayjs()
  ]);
  const [data, setData] = useState<CompanyProposalStats[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchStats = async (customRange?: [Dayjs, Dayjs]) => {
    const [startDate, endDate] = customRange || range;
    if (!startDate || !endDate) {
      message.warning("Lütfen başlangıç ve bitiş tarihini seçiniz.");
      return;
    }
    setLoading(true);
    try {
      const res = await ReportService.getCompanyProposalStats(
        startDate.format("YYYY-MM-DD"),
        endDate.format("YYYY-MM-DD")
      );
      setData(res.data);
    } catch {
      setData([]);
      message.error("Veri alınırken hata oluştu!");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStats(range);
    // eslint-disable-next-line
  }, []);

  return (
    <Card
      title="İş Ortağı Firma/Teklif Durumu"
      extra={
        <Space>
          <RangePicker
            value={range}
            onChange={values => setRange(values as [Dayjs, Dayjs])}
            format="DD/MM/YYYY"
            placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
          />
          <Button
            type="primary"
            onClick={() => fetchStats()}
            loading={loading}
          >
            Raporu Getir
          </Button>
        </Space>
      }
    >
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="accountId"
        bordered
        size="small"
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{
          emptyText: "Veri bulunamadı. Lütfen tarih aralığı seçip sorgulayınız."
        }}
      />
    </Card>
  );
};

export default CompanyProposalStatsTable;
